import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";

import { Card } from "@/components/ui/card";
import { useEffectivePermissions, useHasPermission } from "@/lib/auth/permissions";

interface Props {
  /** Permission code that must be present, e.g. "members.read" */
  permission: string;
}

/**
 * Route-level permission guard. Renders child routes (<Outlet />) when the
 * logged-in user has the required permission; otherwise shows an access-denied
 * card. Use as the `element` of a parent route that wraps protected children.
 */
export function PermissionGuard({ permission }: Props) {
  const { t } = useTranslation();
  const { isLoading } = useEffectivePermissions();
  const allowed = useHasPermission(permission);

  if (isLoading) return null;

  if (!allowed) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <Card className="text-sm text-ink-500 p-6">{t("rbac.noAccess")}</Card>
      </div>
    );
  }

  return <Outlet />;
}
